import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STATS = [
  { value: '500+', label: 'Projects Delivered' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '12+', label: 'Years Experience' },
  { value: '40+', label: 'Expert Engineers' },
];

const FEATURE_CARDS = [
  { icon: 'ri-robot-2-line', title: 'AI-Powered Solutions', desc: 'Machine learning & intelligent automation', color: 'teal' },
  { icon: 'ri-global-line', title: 'Web & Mobile Apps', desc: 'Cross-platform tailored development', color: 'emerald' },
  { icon: 'ri-database-2-line', title: 'Smart Business Tools', desc: 'Inventory, analytics & enterprise systems', color: 'cyan' },
];

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-white">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Light radial gradient accent */}
        <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-cyan-50/60 blur-3xl translate-y-1/4 -translate-x-1/4" />
        {/* Dot grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dot-grid" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="#0d9488" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dot-grid)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" />
              AI-Powered IT Solutions &bull; 25 Years of Excellence
            </div> */}

            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
              Transform Your Business with{' '}
              <span className="text-teal-500">Intelligent</span> Software
            </h1>

            <p className="text-gray-500 text-lg leading-relaxed mb-8 max-w-xl">
              We build tailored AI-driven applications for Web, Windows, and Mobile —
              plus powerful proprietary tools for design, publishing, maritime operations, and inventory management.
            </p>

            <div className="flex flex-wrap gap-4 mb-14">
              <Link
                to="/services"
                className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-all hover:scale-105"
              >
                Explore Our Services
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/contact"
                className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 border border-gray-200 text-gray-700 hover:border-teal-400 hover:text-teal-600 font-semibold px-7 py-3.5 rounded-lg transition-all"
              >
                Get Free Consultation
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-gray-100">
              {STATS.map((stat) => (
                <div key={stat.value} className="text-center">
                  <div className="text-3xl font-extrabold text-teal-500">{stat.value}</div>
                  <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Illustration + Feature Cards */}
          <div
            className={`hidden lg:flex flex-col items-center gap-6 transition-all duration-1000 delay-300 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            {/* Logo display large */}
            <div className="relative flex items-center justify-center w-full">
              <div className="absolute w-72 h-72 rounded-full bg-teal-50 border border-teal-100" />
              <div className="absolute w-56 h-56 rounded-full bg-teal-100/40 border border-teal-200/60" />
              <img
                src="https://static.readdy.ai/image/470712cea6bd611f3a1669acb3b30c19/7df20f577f8643a2dddf858949ce508a.png"
                alt="Company Logo"
                className="relative z-10 w-44 h-44 object-contain drop-shadow-md"
              />
            </div>

            {/* Feature Cards */}
            <div className="flex flex-col gap-3 w-full mt-2">
              {FEATURE_CARDS.map((card) => (
                <div
                  key={card.title}
                  className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl px-5 py-4"
                >
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl bg-teal-50 text-teal-600">
                    <i className={`${card.icon} text-xl`} />
                  </div>
                  <div>
                    <div className="text-gray-900 font-semibold text-sm">{card.title}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{card.desc}</div>
                  </div>
                  <div className="ml-auto w-8 h-8 flex items-center justify-center text-gray-300">
                    <i className="ri-arrow-right-s-line text-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-400 animate-bounce z-10">
        <i className="ri-arrow-down-line text-2xl" />
      </div>
    </section>
  );
}
